import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { renderMentions } from './renderMentions'
import { fullNameOf, useOrgUsers } from './useOrgUsers'
import { computePickerState, filterUsersForPicker, findMentionAt } from './mentionable.helpers'

import './MentionableTextarea.scss'

/**
 * Textarea with inline `@mention` autocomplete. Type `@`, get a popover
 * of users in the org; picking one splices `@Full Name ` at the cursor
 * and records the user_id.
 *
 * Backspace/Delete on a mention removes the whole token (Slack/Linear
 * convention) rather than dropping a single character and leaving a
 * half-broken mention behind.
 *
 * @mention is gated behind the `note-mentions` LaunchDarkly flag (off by
 * default for launch). When off, this is a plain textarea — no picker, no
 * highlight, no token-delete.
 *
 * @param {object} props
 * @param {string} props.value
 * @param {(value: string) => void} props.onChange
 * @param {number[]} [props.mentionedUserIds=[]]
 * @param {(ids: number[]) => void} props.onMentionedChange
 * @param {number} props.organizationId
 * @param {Array<object>} [props.orgUsers] - when omitted, the hook fetches them
 * @param {string} [props.placeholder='Add a note…']
 * @param {number} [props.rows=3]
 * @param {boolean} [props.autoFocus=false] - focus + place caret at end on mount
 * @param {string} [props.testId='mentionable-textarea'] - rendered as data-testid on the textarea
 */
export const MentionableTextarea = ({
  value,
  onChange,
  mentionedUserIds = [],
  onMentionedChange,
  organizationId,
  orgUsers: orgUsersProp,
  placeholder = 'Add a note…',
  rows = 3,
  autoFocus = false,
  testId = 'mentionable-textarea',
}) => {
  const { noteMentions: mentionsEnabled = false } = useFlags()
  const { user_id: currentUserId } = useSelector((state) => state.currentUser)
  const textareaRef = useRef(null)
  const backdropRef = useRef(null)

  // When revealed (collapsed-compose / edit), land the caret in the textarea
  // so the user can type immediately instead of clicking into it.
  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      const el = textareaRef.current
      el.focus()
      el.setSelectionRange(el.value.length, el.value.length)
    }
  }, [])
  const fetchedUsers = useOrgUsers(orgUsersProp ? null : organizationId)
  const users = orgUsersProp ?? fetchedUsers
  const [picker, setPicker] = useState(null) // null or { start, query }

  // Trailing newline prevents the backdrop from collapsing the last line
  // when the user ends the note with a return key. No highlight when the
  // mentions flag is off (pass no users → renderMentions returns plain text).
  const backdropContent = renderMentions(
    `${value}\n`,
    mentionsEnabled ? users : [],
    'mentionable-textarea__mention'
  )

  const syncScroll = () => {
    if (backdropRef.current && textareaRef.current) {
      backdropRef.current.scrollTop = textareaRef.current.scrollTop
    }
  }

  const filteredUsers = useMemo(
    () => (picker ? filterUsersForPicker(users, picker.query, currentUserId) : []),
    [users, picker, currentUserId]
  )

  const refreshPicker = (text, cursor) => {
    if (!mentionsEnabled) {
      return
    }
    setPicker(computePickerState(text, cursor, users, currentUserId))
  }

  const handleChange = (event) => {
    const next = event.target.value
    onChange(next)
    refreshPicker(next, event.target.selectionStart)
  }

  const deleteRange = (range) => {
    const next = value.slice(0, range.start) + value.slice(range.end)
    onChange(next)
    // Only drop the user_id if they're no longer mentioned anywhere.
    const removedUser = users.find((user) => user.id === range.userId)
    if (removedUser) {
      const stillMentioned = next.includes(`@${fullNameOf(removedUser)}`)
      if (!stillMentioned) {
        onMentionedChange(mentionedUserIds.filter((id) => id !== range.userId))
      }
    }
    setPicker(null)
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.focus()
        textareaRef.current.setSelectionRange(range.start, range.start)
      }
    })
  }

  const handleSelectUser = (user) => {
    if (!picker || !textareaRef.current) {
      return
    }
    const name = fullNameOf(user)
    const before = value.slice(0, picker.start)
    const after = value.slice(picker.start + picker.query.length + 1) // +1 for the @
    const insert = `@${name} `
    const next = `${before}${insert}${after}`
    onChange(next)
    if (!mentionedUserIds.includes(user.id)) {
      onMentionedChange([...mentionedUserIds, user.id])
    }
    setPicker(null)
    requestAnimationFrame(() => {
      const newCursor = before.length + insert.length
      textareaRef.current.focus()
      textareaRef.current.setSelectionRange(newCursor, newCursor)
    })
  }

  const handleKeyDown = (event) => {
    // Mentions off → plain textarea: don't intercept keys.
    if (!mentionsEnabled) {
      return
    }
    if (picker && event.key === 'Enter' && !isEmpty(filteredUsers)) {
      event.preventDefault()
      handleSelectUser(filteredUsers[0])
      return
    }
    if (event.key === 'Escape' && picker) {
      setPicker(null)
      event.preventDefault()
      return
    }

    const textarea = event.target
    const noSelection = textarea.selectionStart === textarea.selectionEnd
    if (!noSelection) {
      return
    }

    const cursor = textarea.selectionStart
    if (event.key === 'Backspace') {
      const range = findMentionAt(value, cursor, 'backward', users)
      if (range) {
        event.preventDefault()
        deleteRange(range)
      }
    } else if (event.key === 'Delete') {
      const range = findMentionAt(value, cursor, 'forward', users)
      if (range) {
        event.preventDefault()
        deleteRange(range)
      }
    }
  }

  return (
    <div className="mentionable-textarea">
      <div className="mentionable-textarea__field">
        <div ref={backdropRef} className="mentionable-textarea__backdrop" aria-hidden="true">
          {backdropContent}
        </div>
        <textarea
          ref={textareaRef}
          className="mentionable-textarea__input"
          data-testid={testId}
          placeholder={placeholder}
          value={value}
          rows={rows}
          onChange={(event) => {
            handleChange(event)
            syncScroll()
          }}
          onScroll={syncScroll}
          onKeyDown={handleKeyDown}
          onClick={(event) => refreshPicker(event.target.value, event.target.selectionStart)}
          onKeyUp={(event) => {
            if (event.key !== 'Escape') {
              refreshPicker(event.target.value, event.target.selectionStart)
            }
          }}
        />
      </div>
      {picker && !isEmpty(filteredUsers) && (
        <ul
          className="mentionable-textarea__menu"
          data-testid="mentionable-textarea-menu"
          role="listbox"
        >
          {filteredUsers.map((user) => {
            const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim()
            const label = fullName || user.username
            return (
              <li key={user.id} className="mentionable-textarea__menu-item">
                <button
                  type="button"
                  className="mentionable-textarea__menu-button"
                  onClick={() => handleSelectUser(user)}
                  data-testid={`mention-option-${user.id}`}
                >
                  <span className="mentionable-textarea__menu-label">{label}</span>
                  {fullName && user.username && (
                    <span className="mentionable-textarea__menu-username">{user.username}</span>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
